import {effect, inject, Injectable, resource, Type} from '@angular/core';
import {DialogService} from "primeng/dynamicdialog";
import {HttpClient} from '@angular/common/http';
import {environment} from "../../../../environments/environment";
import {NotificationDto} from "homey-housing-lottery-app-dto";
import typia from "typia";
import {UserService} from "../user.service";
import {lastValueFrom, pairwise, Subscription} from "rxjs";
import {StorageKey, StorageService} from "../storage.service";
import {
  MultiPageNotificationComponent,
  NotificationPage,
  NotificationPageData
} from "./multi-page-notification/multi-page-notification.component";
import {HomeyNotification, HomeyNotificationType} from "../../model/notification";
import {
  NewClosedApplicationsDialogContentComponent
} from "./notification-dialog-content/new-closed-applications-dialog-content/new-closed-applications-dialog-content.component";
import {
  LotteryNumberReceivedApplicationsDialogContentComponent
} from "./notification-dialog-content/lottery-number-received-applications-dialog-content/lottery-number-received-applications-dialog-content.component";
import {
  NewEligibleLotteriesDialogContentComponent
} from "./notification-dialog-content/new-eligible-lotteries-dialog-content/new-eligible-lotteries-dialog-content.component";
import {
  NotSelectedApplicationsDialogContentComponent
} from "./notification-dialog-content/not-selected-applications-dialog-content/not-selected-applications-dialog-content.component";
import {
  SelectedApplicationsDialogContentComponent
} from "./notification-dialog-content/selected-applications-dialog-content/selected-applications-dialog-content.component";
import {MessageDialogContentComponent} from "./message-dialog-content/message-dialog-content.component";
import {
  AutoApplySubmittedDialogContentComponent
} from "./notification-dialog-content/auto-apply-submitted-lotteries-dialog-content/auto-apply-submitted-dialog-content.component";
import {
  ApplicationStatusUpdateDialogContentComponent
} from "./notification-dialog-content/application-status-update-dialog-content/application-status-update-dialog-content.component";
import {
  RateNotificationDialogContentComponent
} from "./notification-dialog-content/rate-notification-dialog-content/rate-notification-dialog-content.component";
import {ABService} from "../a-b.service";

type NotificationOfType<key> = Extract<HomeyNotification, { type: key }>;

const notificationTypeToPageMap: {
  [key in HomeyNotificationType]: {
    component: Type<NotificationPage<NotificationOfType<key>>>,
    disableContinue?: boolean,
  }
} = {
  "auto-apply-submitted": {
    component: AutoApplySubmittedDialogContentComponent,
  },
  "lottery-number-received": {
    component: LotteryNumberReceivedApplicationsDialogContentComponent,
  },
  "new-eligible-lotteries": {
    component: NewEligibleLotteriesDialogContentComponent,
  },
  "new-closed-applications": {
    component: NewClosedApplicationsDialogContentComponent,
  },
  "not-selected": {
    component: NotSelectedApplicationsDialogContentComponent,
  },
  "selected": {
    component: SelectedApplicationsDialogContentComponent,
  },
  "message": {
    component: MessageDialogContentComponent,
  }
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly dialogService = inject(DialogService);
  private readonly http = inject(HttpClient);
  private readonly userService = inject(UserService);
  private readonly storageService = inject(StorageService);
  private readonly abService = inject(ABService);
  private readonly statusUpdateStorageKey: StorageKey = 'STATUS_UPDATE_NOTIFICATION_LAST_SHOWN';
  private readonly statusUpdateActivationDateKey: StorageKey = 'STATUS_UPDATE_NOTIFICATION_ACTIVATED_DATE';

  public openNotificationDialog(
    pages: NotificationPageData<any>[],
    options: { closable?: boolean } = {}
  ) {
    return this.dialogService.open(
      MultiPageNotificationComponent,
      {
        showHeader: false,
        closable: options.closable ?? true,
        modal: true,
        focusOnShow: false,
        width: 'calc(min(100vw - 10px, 500px))',
        inputValues: {
          pages: pages,
          closable: options.closable ?? true,
        },
      }
    );
  }

  private userSubscription?: Subscription;

  public async retrieveData() {
    if(!this.userSubscription) {
      this.userSubscription = this.userService.user$.pipe(
        pairwise()
      ).subscribe(([previousUser, currentUser]) => {
        const authorized = previousUser.status === "unauthorized" && currentUser.status !== "unauthorized";

        if (authorized) {
          void this.retrieveData();
        }
      });
    }

    if(this.userService.isUserPostSignupFlow()) {
      console.log("Retrieving notifications...");
      const response = await lastValueFrom(this.http.get<unknown>(`${environment.apiUrl}/api/private/notifications`));

      if (!Array.isArray(response)) {
        return;
      }

      await this.handleNotifications(response.filter((notification) => typia.is<NotificationDto>(notification)));
    }
  }

  private async handleNotifications(notifications: NotificationDto[]) {
    if (notifications.length > 0) {
      this.http.put(`${environment.apiUrl}/api/private/notifications/mark-as-read`, {}).subscribe();
    }

    console.log('Received notifications:', notifications);


    const pages: NotificationPageData<any>[] = notifications
      .map((notification) => {
        const notificationType = notification.type;
        console.log('Processing notification of type:', notificationType);
        const pageData = notificationTypeToPageMap[notificationType];

        if (!pageData?.component) {
          console.error(`Unknown notification type received: ${notificationType}`);
          return undefined;
        }

        return {
          component: pageData.component,
          data: notification,
          disableContinue: pageData.disableContinue,
        };
      })
      .filter((page) => !!page);

    console.log('Prepared notification pages:', pages);

    const hasApplicationResolution = notifications.some(n =>
      n.type === 'new-closed-applications' || n.type === 'lottery-number-received'
    );
    const shouldShowStatusUpdate = await this.shouldShowStatusUpdateToday();

    if (shouldShowStatusUpdate) {
      pages.push({
        component: ApplicationStatusUpdateDialogContentComponent,
        data: null,
      });
      await this.storageService.setItem(this.statusUpdateStorageKey, new Date().toISOString().slice(0, 10));
    }

    if (this.shouldShowRatePage({ hasApplicationResolution })) {
      pages.push({
        component: RateNotificationDialogContentComponent,
        data: { anonymousId: undefined },
        disableContinue: true,
      });
    }

    if(pages.length === 0) {
      return;
    }

    this.openNotificationDialog(pages, { closable: false });
  }

  private async shouldShowStatusUpdateToday(): Promise<boolean> {
    const user = this.userService.user();

    if (!user || user.status !== 'active') {
      return false;
    }

    await this.ensureActivationDateStored();

    const today = new Date().toISOString().slice(0, 10);
    const lastShown = await this.storageService.getItem(this.statusUpdateStorageKey);
    const activationDate = await this.storageService.getItem(this.statusUpdateActivationDateKey);

    if (activationDate === today) {
      return false;
    }

    return lastShown !== today;
  }

  private async ensureActivationDateStored() {
    const activationDate = await this.storageService.getItem(this.statusUpdateActivationDateKey);

    if (activationDate === undefined) {
      await this.storageService.setItem(this.statusUpdateActivationDateKey, new Date().toISOString().slice(0, 10));
    }
  }

  private shouldShowRatePage(params: { hasApplicationResolution: boolean }): boolean {
    if (this.abService.reviewPrompt() !== 'dialog') {
      return false;
    }

    const user = this.userService.user();

    if (!user || user.status !== 'active') {
      return false;
    }

    const reviewInfo = user.reviewInfo;
    const now = new Date();
    const oneMonthMs = 1000 * 60 * 60 * 24 * 30;

    if (reviewInfo.type === 'not-asked') {
      return params.hasApplicationResolution;
    }

    const lastAsked = new Date(reviewInfo.lastAsked).getTime();
    return now.getTime() - lastAsked >= oneMonthMs;
  }
}
